import React, { useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, Copy, Send, Mail, Phone, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DialogShell from '../kit/DialogShell';
import { api, isSpendApprovalError } from '../api';
import { toast } from '../../hooks/use-toast';
import { fmt$, errMsg } from './constants';
import { copyText as copyToClipboard } from '../clipboard';
import PhoneInput from '../shared/PhoneInput';
import { fromApi } from '../kit/time';
import { quietHoursDetail, confirmQuietHours } from '../quietHours';
import ContactSearchInput from '../shared/ContactSearchInput';
import useTextRecipient, { contactFields } from '../shared/useTextRecipient';

// ============================================================================
// BakeoffSendModal — deliver the Program Bakeoff PDF via email/SMS
// ============================================================================
export function BakeoffSendModal({ basePayload, winner, onClose }) {
  const [borrowerName, setBorrowerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [picked, setPicked] = useState(null); // contact last picked from the name search (ticket 174)
  const { line: recipient, blocked } = useTextRecipient({ phone, email, name: borrowerName, picked });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Ticket 174: a pick fills all three fields (still editable); "+ New contact"
  // keeps the typed name and empties phone/email for typing.
  const pickContact = (c) => {
    const f = contactFields(c);
    setPicked(c);
    setBorrowerName(f.name);
    setEmail(f.email);
    setPhone(f.phone);
  };
  const newPerson = (text) => {
    setPicked(null);
    setBorrowerName(text);
    setEmail('');
    setPhone('');
  };

  const send = async () => {
    if (!borrowerName.trim()) { toast({ title: 'Borrower name is required' }); return; }
    if (!email && !phone) { toast({ title: 'Provide at least an email or phone' }); return; }
    setBusy(true);
    try {
      const body = {
        ...basePayload,
        borrower_name: borrowerName,
        borrower_email: email || null,
        borrower_phone: phone || null,
        personal_note: note || null,
      };
      let data;
      try {
        ({ data } = await api.post('/income/form-91/compare/send', body));
      } catch (e) {
        // Ticket 171: outside the send window the server refuses until the LO confirms.
        const qh = quietHoursDetail(e);
        if (!qh) throw e;
        if (!(await confirmQuietHours(qh))) return; // finally clears busy
        ({ data } = await api.post('/income/form-91/compare/send', { ...body, confirm_quiet_hours: true }));
      }
      setResult(data);
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Send failed', description: errMsg(e, e.message) });
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text) => {
    if (await copyToClipboard(text)) toast({ title: 'Copied', description: 'Text copied to clipboard' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  return (
    <DialogShell
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Program Bakeoff PDF"
      description="Send the side-by-side agency comparison to the borrower by email or SMS."
      size="lg"
      data-testid="bakeoff-send-modal"
    >
        {!result ? (
          <div className="space-y-4">
            {winner && (
              <div className="rounded-lg p-3 border" style={{ background: '#ECFDF5', borderColor: '#A7F3D0' }}>
                <div className="text-[11px] uppercase font-bold text-emerald-800">Best Program (will be highlighted)</div>
                <div className="text-sm font-semibold text-emerald-900">
                  {winner.display_name} · {fmt$(winner.qualifying_monthly_income)}/mo
                </div>
              </div>
            )}
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500 block mb-1">Borrower Name</label>
              <ContactSearchInput
                value={borrowerName}
                onChange={setBorrowerName}
                onPick={pickContact}
                onCreateNew={newPerson}
                testid="bakeoff-borrower-name"
                placeholder="Jane Doe"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-brand-navy"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500 block mb-1">
                  <Mail size={11} className="inline mr-1" />Email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="bakeoff-borrower-email"
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500 block mb-1">
                  <Phone size={11} className="inline mr-1" />Phone
                </label>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  data-testid="bakeoff-borrower-phone"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-brand-navy"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500 block mb-1">Personal Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="bakeoff-personal-note"
                placeholder="Jane — really excited to help you close on that duplex. Let me know which program you'd like to explore first!"
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-brand-navy text-sm"
              />
            </div>
            {recipient && (
              <div className="text-[12px] text-slate-600" data-testid="bakeoff-recipient">{recipient}</div>
            )}
            <Button
              onClick={send}
              disabled={busy || blocked}
              data-testid="bakeoff-send-btn"
              variant="navy"
              className="w-full"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Generating & sending PDF…</> : <><Send size={16} /> Generate PDF & Deliver</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Delivery status */}
            <div className="rounded-lg p-3" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={16} style={{ color: '#059669' }} />
                <span className="text-sm font-semibold text-emerald-800">PDF generated successfully</span>
              </div>
              <div className="text-xs text-emerald-700 pl-6 space-y-0.5">
                <div>Email: <b>{result.delivered.email ? 'Delivered' : 'Not delivered (fallback)'}</b></div>
                <div>SMS: <b>{result.delivered.sms ? 'Delivered' : result.sms_blocked ? 'Blocked' : ['ghl_unconfirmed', 'ghl_unconfirmed_hold'].includes(result.sms_reason) ? 'Not confirmed' : result.sms_reason === 'duplicate' ? 'Already sent' : 'Not delivered (fallback)'}</b></div>
                {result.pdf_expires_at && (
                  <div className="mt-1 text-slate-600">
                    Public link expires: <b>{fromApi(result.pdf_expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</b>
                  </div>
                )}
              </div>
            </div>

            {/* PDF link */}
            <div className="rounded-lg p-3 bg-slate-50 border border-slate-200">
              <div className="text-[11px] uppercase font-semibold text-slate-500 mb-2 flex items-center gap-1">
                <Link2 size={11} /> Public PDF URL
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={result.pdf_url}
                  data-testid="bakeoff-pdf-url"
                  className="flex-1 w-auto h-auto px-2 py-1.5 text-xs md:text-xs bg-white font-mono text-slate-700 truncate"
                />
                <button
                  onClick={() => copyText(result.pdf_url)}
                  data-testid="bakeoff-copy-url"
                  className="p-1.5 rounded bg-slate-200 hover:bg-slate-300"
                >
                  <Copy size={12} className="text-slate-700" />
                </button>
                <a
                  href={result.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bakeoff-open-pdf"
                  className="px-3 py-1.5 rounded bg-brand-navy text-white text-xs font-semibold"
                >
                  Open
                </a>
              </div>
            </div>

            {/* Fallback bodies */}
            {result.fallback_instructions && (
              <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
                <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1 flex items-center gap-1">
                  <AlertTriangle size={11} /> Manual delivery needed
                </div>
                <div className="text-xs text-amber-900">{result.fallback_instructions}</div>
              </div>
            )}

            {/* Email body preview */}
            {result.email_body && (
              <div>
                <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1 flex items-center justify-between">
                  <span><Mail size={11} className="inline mr-1" />Email Body (copyable)</span>
                  <button
                    onClick={() => copyText(`Subject: ${result.email_subject}\n\n${result.email_body}`)}
                    data-testid="bakeoff-copy-email"
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
                  >
                    <Copy size={9} className="inline mr-1" />Copy
                  </button>
                </div>
                <div className="rounded-lg p-3 bg-slate-50 border border-slate-200 text-xs whitespace-pre-wrap font-mono text-slate-700 max-h-40 overflow-y-auto">
                  <b>Subject:</b> {result.email_subject}
                  {'\n\n'}
                  {result.email_body}
                </div>
              </div>
            )}
            {result.email_notice && !result.email_body && (
              <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
                <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1 flex items-center gap-1">
                  <AlertTriangle size={11} /> Email not sent
                </div>
                <div className="text-xs text-amber-900">{result.email_notice}</div>
              </div>
            )}
            {result.sms_notice && (
              <div className="rounded-lg p-3 bg-amber-50 border border-amber-200" data-testid="bakeoff-sms-notice">
                <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1 flex items-center gap-1">
                  <AlertTriangle size={11} /> {['ghl_unconfirmed', 'ghl_unconfirmed_hold'].includes(result.sms_reason) ? 'Text not confirmed' : result.sms_reason === 'duplicate' ? 'Text already sent' : 'Text not sent'}
                </div>
                <div className="text-xs text-amber-900">{result.sms_notice}</div>
              </div>
            )}
            {phone && result.sms_body && (
              <div>
                <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1 flex items-center justify-between">
                  <span><Phone size={11} className="inline mr-1" />SMS Body (copyable)</span>
                  <button
                    onClick={() => copyText(result.sms_body)}
                    data-testid="bakeoff-copy-sms"
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
                  >
                    <Copy size={9} className="inline mr-1" />Copy
                  </button>
                </div>
                <div className="rounded-lg p-3 bg-slate-50 border border-slate-200 text-xs whitespace-pre-wrap font-mono text-slate-700">
                  {result.sms_body}
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              data-testid="bakeoff-done-btn"
              className="w-full py-2.5 rounded-lg font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition"
            >
              Done
            </button>
          </div>
        )}
    </DialogShell>
  );
}
